import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard,
  FileText,
  Briefcase,
  Settings,
  Calendar,
  Users,
  Fingerprint,
  LogOut,
  Menu,
  X,
  ClipboardList,
  BarChart3,
  Factory,
  ShieldCheck,
  ClipboardCheck,
  FileBarChart,
  FileSpreadsheet,
  Package,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  UserCog,
  Shield,
  ScrollText,
  Contact,
  Wrench,
  AlertTriangle,
  Activity,
  History,
  Ruler,
  Gauge,
  Upload,
  UserCheck,
  Flame
} from 'lucide-react';
import { canAccessNavItem } from '@/utils/permissions';
import { isCalibrationEnabled } from '@/utils/features';
import NotificationBell from '@/components/NotificationBell';

const Layout = ({ user, onLogout }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const navigation = [
    { 
      name: user?.role === 'Supervisor' ? 'My Workload' : 'Dashboard', 
      path: '/', 
      icon: LayoutDashboard,
      permission: 'dashboard.view'
    },
    { 
      name: 'Daily Production Entry', 
      path: '/daily-production-entry', 
      icon: ClipboardList,
      permission: 'dailyProduction.view'
    },
    { 
      name: 'Job Cards', 
      path: '/jobs', 
      icon: Briefcase,
      permission: 'jobCards.view'
    },
    { 
      name: 'Quality Gate Inspection', 
      path: '/quality-gate-inspection', 
      icon: ShieldCheck, 
      permission: 'qualityGate.view'
    },
    { 
      name: 'In-line Quality', 
      path: '/inline-quality', 
      icon: ClipboardCheck, 
      permission: 'inlineQuality.view'
    },
    { 
      name: 'Drawing Management', 
      path: '/drawing-management', 
      icon: FileSpreadsheet, 
      permission: 'drawingManagement.view'
    },
    { 
      name: 'GRN (Material Receipt)', 
      path: '/grn', 
      icon: Package, 
      permission: 'grn.view'
    },
    { 
      name: 'Reports', 
      icon: FileBarChart,
      isSection: true,
      children: [
        { 
          name: 'Shift Reports', 
          path: '/shift-reports', 
          icon: BarChart3, 
          permission: 'shiftReports.view'
        },
        { 
          name: 'Dispatch Reports', 
          path: '/dispatch-reports', 
          icon: Package, 
          permission: 'dispatchReports.view'
        },
        { 
          name: 'WIP Inventory', 
          path: '/wip-inventory', 
          icon: Factory, 
          permission: 'wipInventory.view'
        },
        { 
          name: 'Raw Material Inventory', 
          path: '/raw-material-inventory', 
          icon: Package, 
          permission: 'rawMaterialInventory.view'
        },
        { 
          name: 'Quality Gate Reports', 
          path: '/quality-gate-reports', 
          icon: FileBarChart, 
          permission: 'qualityGate.reports'
        },
        { 
          name: 'Quality Analytics', 
          path: '/quality-analytics', 
          icon: TrendingUp, 
          permission: 'qualityAnalytics.any'
        },
        { 
          name: 'Heat Treatment', 
          path: '/heat-treatment-report', 
          icon: Flame, 
          adminOnly: true
        },
      ]
    },
    { 
      name: 'Machines', 
      path: '/machines', 
      icon: Settings, 
      permission: 'machines.view'
    },
    { 
      name: 'HR', 
      icon: Contact,
      isSection: true,
      children: [
        { 
          name: 'Employee Master', 
          path: '/employees', 
          icon: Contact, 
          permission: 'employees.view'
        },
        { 
          name: 'Attendance', 
          path: '/attendance', 
          icon: Fingerprint, 
          permission: 'employees.view'
        },
      ]
    },
    { 
      name: 'Maintenance', 
      icon: Wrench,
      isSection: true,
      children: [
        { 
          name: 'Dashboard', 
          path: '/maintenance', 
          icon: Activity, 
          permission: 'breakdowns.view'
        },
        { 
          name: 'Breakdowns', 
          path: '/maintenance/breakdowns', 
          icon: AlertTriangle, 
          permission: 'breakdowns.view'
        },
        { 
          name: 'Machine History', 
          path: '/maintenance/machine-history', 
          icon: History, 
          permission: 'machineHistory.view'
        },
        { 
          name: 'Analytics', 
          path: '/maintenance/analytics', 
          icon: TrendingUp, 
          permission: 'maintenanceAnalytics.view'
        },
      ]
    },
    {
      name: 'Calibration',
      icon: Gauge,
      isSection: true,
      featureEnabled: isCalibrationEnabled(),
      children: [
        {
          name: 'Dashboard',
          path: '/calibration',
          icon: Activity,
          permission: 'calibration.view',
        },
        {
          name: 'Master Calibration Register',
          path: '/calibration/gauges',
          icon: Ruler,
          permission: 'calibration.view',
        },
        {
          name: 'Issuance Log',
          path: '/calibration/issuances',
          icon: UserCheck,
          permission: 'calibration.view',
        },
        {
          name: 'Schedule',
          path: '/calibration/schedule',
          icon: Calendar,
          permission: 'calibration.view',
        },
        {
          name: 'My Gauges',
          path: '/calibration/my-gauges',
          icon: Contact,
          permission: 'calibration.view_my_gauges',
        },
        {
          name: 'Settings',
          path: '/calibration/settings',
          icon: Settings,
          permission: 'calibration.configure',
        },
      ]
    },
    { 
      name: 'Users', 
      path: '/users', 
      icon: Users, 
      permission: 'userManagement.view'
    },
    { 
      name: 'Supervisor Assignments', 
      path: '/supervisor-assignments', 
      icon: UserCog, 
      permission: 'supervisorManagement.view'
    },
    { 
      name: 'Admin Settings', 
      path: '/admin-settings', 
      icon: Shield, 
      adminOnly: true
    },
    { 
      name: 'Audit Logs', 
      path: '/audit-logs', 
      icon: ScrollText, 
      adminOnly: true
    }
  ];

  const filterNavItems = (items) => {
    return items.filter(item => {
      // Hostname-based feature flags (e.g. Calibration hidden in production)
      if (item.featureEnabled === false) {
        return false;
      }
      // Handle admin-only items
      if (item.adminOnly) {
        return user?.role === 'Admin';
      }
      if (item.isSection && item.children) {
        item.children = filterNavItems(item.children);
        return item.children.length > 0;
      }
      return canAccessNavItem(user, item);
    });
  };

  const filteredNav = filterNavItems(navigation);

  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName.toLowerCase()]: !prev[sectionName.toLowerCase()]
    }));
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    // Exact match for leaf items to prevent double-highlighting in sections
    return location.pathname === path;
  };

  const isSectionActive = (section) => {
    if (!section.children) return false;
    // Use prefix match for section-level detection (highlights parent section)
    return section.children.some(child => location.pathname === child.path || location.pathname.startsWith(child.path + '/'));
  };

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 lg:transform-none ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-6 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_7886508f-18cf-499c-9228-09581ab9ee95/artifacts/b13a0301b3439545_ABOK_SPRINGS_LOGO_navy.webp" 
                alt="ABOK Springs Logo" 
                className="h-6 w-auto"
                data-testid="sidebar-logo"
              />
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
              data-testid="close-sidebar-btn"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {filteredNav.map((item) => {
              const Icon = item.icon;
              
              // Handle section with children (like Reports)
              if (item.isSection && item.children) {
                const sectionKey = item.name.toLowerCase();
                const isExpanded = expandedSections[sectionKey];
                const sectionIsActive = isSectionActive(item);
                
                return (
                  <div key={item.name} className="space-y-1">
                    <button
                      onClick={() => toggleSection(item.name)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200 ${
                        sectionIsActive
                          ? 'bg-slate-100 text-slate-900'
                          : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5" />
                        <span className="font-medium">{item.name}</span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                    
                    {/* Sub-items */}
                    {isExpanded && (
                      <div className="ml-4 space-y-1 pl-4 border-l-2 border-slate-200">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          // Use exact match for leaf items to prevent double-highlighting
                          // Only use prefix match for items that have sub-routes (like machine-history/:id)
                          const childActive = (child.path === '/maintenance' || child.path === '/calibration')
                            ? location.pathname === child.path
                            : (location.pathname === child.path || location.pathname.startsWith(child.path + '/'));
                          return (
                            <Link
                              key={child.path}
                              to={child.path}
                              onClick={() => setSidebarOpen(false)}
                              data-testid={`nav-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                              className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 ${
                                childActive
                                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                                  : 'text-slate-600 hover:bg-slate-100'
                              }`}
                            >
                              <ChildIcon className="h-4 w-4" />
                              <span className="text-sm font-medium">{child.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              // Handle regular navigation items
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    active
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-3 px-2">
              <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.role}</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full justify-start gap-2"
              onClick={onLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-4 lg:px-8">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden mr-4"
            onClick={() => setSidebarOpen(true)}
            data-testid="open-sidebar-btn"
          >
            <Menu className="h-6 w-6" />
          </Button>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-slate-800">
              {(() => {
                // Check regular items first
                const regularItem = filteredNav.find(item => !item.isSection && isActive(item.path));
                if (regularItem) return regularItem.name;
                
                // Check nested items
                for (const item of filteredNav) {
                  if (item.isSection && item.children) {
                    const childItem = item.children.find(child => isActive(child.path));
                    if (childItem) return childItem.name;
                  }
                }
                
                return 'Dashboard';
              })()}
            </h2>
          </div>
          <NotificationBell />
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <div className="p-4 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
